
import { useState, useEffect } from "react";
import { MFOItem } from "@/types/kanban";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { ArrowRight, MoveRight } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";

interface TransferMFOModalProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  mfo: MFOItem;
  currentColumnId: string;
  onTransfer: (newColumnId: string) => void;
}

interface ColumnOption {
  id: string;
  label: string;
}

const TransferMFOModal = ({
  isOpen,
  onOpenChange,
  mfo,
  currentColumnId,
  onTransfer,
}: TransferMFOModalProps) => {
  const [selectedColumnId, setSelectedColumnId] = useState<string | undefined>(undefined);
  const [columns, setColumns] = useState<ColumnOption[]>([]);
  const { toast } = useToast();

  // Fetch available columns when modal opens
  useEffect(() => {
    const fetchColumns = async () => {
      if (isOpen) {
        try {
          const { data, error } = await supabase
            .from("kanban_columns")
            .select("id, title")
            .order("order_index");
            
          if (error) throw error;
          
          if (data) {
            setColumns(data.map(col => ({
              id: col.id,
              label: col.title
            })));
          }
        } catch (err) {
          console.error("Error fetching columns:", err);
        }
      }
    };
    
    fetchColumns();
    
    // Reset selected column when modal opens/closes
    if (!isOpen) {
      setSelectedColumnId(undefined);
    }
  }, [isOpen]);

  const handleTransfer = () => {
    if (!selectedColumnId) {
      toast({
        title: "Select a column",
        description: "Please select a destination column",
        variant: "destructive",
      });
      return;
    }

    if (selectedColumnId === currentColumnId) {
      toast({
        title: "Same column",
        description: "Please select a different column",
        variant: "destructive",
      });
      return;
    }

    onTransfer(selectedColumnId);
    
    toast({
      title: "MFO Transferred",
      description: `"${mfo.title}" moved to ${columns.find(col => col.id === selectedColumnId)?.label}`,
    });
  };

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>Transfer MFO</DialogTitle>
          <DialogDescription>
            Move "{mfo.title}" to a different column.
          </DialogDescription>
        </DialogHeader>

        <div className="grid gap-4 py-4">
          <div className="space-y-2">
            <h4 className="text-sm font-medium">Current status</h4>
            <div className="px-3 py-2 bg-muted rounded-md text-sm">
              {columns.find(col => col.id === currentColumnId)?.label || "Unknown"}
            </div>
          </div>

          <div className="flex items-center justify-center text-muted-foreground">
            <MoveRight className="h-6 w-6" />
          </div>

          <div className="space-y-2">
            <h4 className="text-sm font-medium">New status</h4>
            <Select onValueChange={setSelectedColumnId}>
              <SelectTrigger>
                <SelectValue placeholder="Select a status" />
              </SelectTrigger>
              <SelectContent>
                {columns
                  .filter(col => col.id !== currentColumnId)
                  .map((column) => (
                    <SelectItem key={column.id} value={column.id}>
                      {column.label}
                    </SelectItem>
                  ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button 
            onClick={handleTransfer} 
            disabled={!selectedColumnId || selectedColumnId === currentColumnId}
          >
            <ArrowRight className="mr-2 h-4 w-4" />
            Transfer
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default TransferMFOModal;
